<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import { BvBtn, BvTextInput } from '@bv/components/form';
  import { vLongPress } from '@bv/directives';
  import { useSipStore } from '@bv/stores';
  import { sip } from '@bv/utils';

  import CallIcon from './partials/CallIcon.vue';
  import ClearIcon from './partials/ClearIcon.vue';

  const sipStore = useSipStore();

  const isRegistered = computed<boolean>(() => sipStore.isRegistered);

  defineOptions({
    directives: {
      longPress: vLongPress,
    },
  });

  withDefaults(defineProps<{ size?: 'sm' | 'md' }>(), {
    size: 'md',
  });

  const model = defineModel<string>({ default: '' });

  const numbers = ref(['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']);

  watch(
    () => model.value,
    (newValue) => {
      model.value = newValue.replace(/[^0-9*#+]/g, '');
    },
  );

  const addNum = (number: string): void => {
    model.value += number;
    sendDTMF(number);
  };

  const sendDTMF = (number: string): void => {
    if (numbers.value.includes(number) || number === '+') {
      sip.sendDTMF(number);
    }
  };

  const clearNumber = (): void => {
    model.value = '';
  };

  const deleteNumber = (): void => {
    model.value = model.value.slice(0, -1);
  };

  const call = (): void => {
    sip.call(model.value).then(() => {
      clearNumber();
      emit('on-call');
    });
  };

  const emit = defineEmits<{
    (event: 'on-call'): void;
  }>();
</script>

<template>
  <form
    class="grid grid-cols-3 gap-3"
    dir="ltr"
    @submit.prevent="call"
  >
    <bv-text-input
      v-model="model"
      autofocus
      bg-color="var(--color-gray-50)"
      class="input col-span-3 [&_.v-field\_\_input]:text-left [&_.v-field\_\_input]:text-gray-700 [&_.v-field\_\_outline>*]:border-0 [&.input_.v-field\_\_outline]:shadow-none"
      color="var(--color-red-300)"
      dir="ltr"
      placeholder="ex: 12345"
      :size
      type="text"
      @keydown="sendDTMF($event.key)"
    >
      <template #append-inner>
        <clear-icon
          v-if="model"
          v-long-press="clearNumber"
          class="-me-1 h-6 w-6 min-w-6 cursor-pointer text-gray-500 hover:text-gray-600"
          @click="deleteNumber"
        />
      </template>
    </bv-text-input>
    <bv-btn
      v-for="number in numbers"
      :key="number"
      :class="size === 'md' ? 'h-10' : 'h-8'"
      color="var(--color-base-400)"
      variant="secondary"
      @click="addNum(number)"
    >
      <span class="text-base-800">
        {{ number }}
      </span>
    </bv-btn>
    <bv-btn
      class="col-span-3"
      :class="size === 'md' ? 'h-12' : 'h-10'"
      color="var(--color-green-400)"
      :disabled="!model || !isRegistered"
      type="submit"
    >
      <call-icon />
    </bv-btn>
  </form>
</template>

<style lang="scss" scoped>
  :deep(.v-field) {
    direction: ltr;
  }
</style>
